import {Column, Row} from '@vidazoo/ui-kit';
import clsx from 'clsx';
import RowCompareToggle from './components/RowCompareToggle';
import React from 'react';
import {observer} from 'mobx-react';
import BoxLoader from '../../../../common/components/box-loader/BoxLoader';
import For from '../../../../common/components/for/For';
import useReportingModel from '../../common/hooks/useReportingModel';
import useReportingParamsManager from "../../common/hooks/useReportingParamsManager";
import ReportFieldValueCell from "./components/ReportFieldValueCell";
import ReportGroupValueCell from "./components/ReportGroupValueCell";
import theme from '../../view/theme.module.scss';
import useReportingBuildManager from "../../common/hooks/useReportingBuildManager";

export interface IReportingTableRowProps {
    itemIndex: number;
    viewportIndex: number;
    style: React.CSSProperties;
}

export const LOADING_ROW_HEIGHT = 24;
export const LOADING_ROW_WIDTH = 250;

function ReportingTableRow({itemIndex, style}: IReportingTableRowProps) {
    const model = useReportingModel();
    const {prepareOriginFields, params, quickFilter, quickConstraint} = useReportingParamsManager();
    const {requestedGroups} = useReportingBuildManager()

    if (model.isLoading || !model.data) {
        return (
            <Row style={style} key={itemIndex} index={itemIndex}>
                <For count={params.groups.length + prepareOriginFields.length}>
                    <Column width={LOADING_ROW_WIDTH} className='flex items-center justify-center p-4'>
                        <BoxLoader.Row height={LOADING_ROW_HEIGHT}/>
                    </Column>
                </For>
            </Row>
        );
    }
    const row = model.maskItems.at(itemIndex);
    const groups = requestedGroups
        .map((paramGroup) => row.groups.find(group => paramGroup.value === group.key))
        .filter(Boolean);
    const fields = params.fields
        .map((paramField) => row.fields.find(field => paramField.value === field.key))
        .filter(Boolean);

    const isBaseline = model.baselineRowId === row.id;
    const baselineFields = isBaseline ? null : model.baselineFields;

    return (
        <Row style={style} key={row.id} index={itemIndex} data-reporting-row-id={row.id}
             data-baseline-row={isBaseline || undefined}
             className={clsx(theme.row, itemIndex % 2 && theme.even, isBaseline && theme.baselineRow)}>
            {groups.map((group, index) => (
                <Column key={group.key} colKey={group.key}>
                    {index === 0 ? (
                        <div className={theme.rowStart}>
                            <ReportGroupValueCell group={group} onQuickFilter={quickFilter}/>
                            <RowCompareToggle isBaseline={isBaseline}
                                              onCompare={() => model.setBaselineRow(row.id)}
                                              onStop={model.clearBaselineRow}/>
                        </div>
                    ) : <ReportGroupValueCell group={group} onQuickFilter={quickFilter}/>}
                </Column>
            ))}
            {fields.map((field) => (
                <Column key={field.key} colKey={field.key}>
                    <ReportFieldValueCell field={field} onQuickConstraint={quickConstraint}
                                          baselineField={baselineFields?.get(field.key)}/>
                </Column>
            ))}
        </Row>
    )
}

export default observer(ReportingTableRow);
